import React, { useState, useEffect, useCallback, useRef } from "react";
import ConfirmModal from "../components/ConfirmModal";
import { api } from "../utils/api";
import { pct, fmtTime } from "../utils/helpers";

export default function Servers({ onRefresh }) {
  const nameInputRef = useRef(null);
  const [agents,    setAgents]    = useState(() => { try { return JSON.parse(localStorage.getItem("cached_agents") || "[]"); } catch { return []; } });
  const [campaigns, setCampaigns] = useState([]);
  const [addModal,  setAddModal]  = useState(false);

  useEffect(() => {
    if (addModal) {
      // slight delay so the modal transition has started before focusing (avoids layout jank / unwanted scroll on mobile)
      const t = setTimeout(() => nameInputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [addModal]);
  const [keyModal,  setKeyModal]  = useState(null);
  const [form,      setForm]      = useState({ name: "", capacity: "60", notes: "" });
  const [err,       setErr]       = useState("");
  const [loading,   setLoading]   = useState(false);
  const [fetching,  setFetching]  = useState(true);
  const [copied,    setCopied]    = useState(false);
  const [copiedLinux, setCopiedLinux] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // agent object
  const [confirmRegen, setConfirmRegen] = useState(null); // agent object

  const SERVER = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "https://api.zoommembers.com";

  const load = useCallback(async () => {
    try { const [ag, cp] = await Promise.all([api.getAgents(), api.getCampaigns()]); setAgents(ag); setCampaigns(cp); } catch {} finally { setFetching(false); }
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 6000); return () => clearInterval(i); }, [load]);

  const online      = agents.filter(a => a.status === "online");
  const totalCap    = online.reduce((a, ag) => a + ag.capacity, 0);
  const totalActive = campaigns.filter(c => c.status === "running").reduce((a, c) => a + (c.active || 0), 0);

  const createAgent = async () => {
    if (!form.name.trim()) { setErr("Name required"); return; }
    const cap = parseInt(form.capacity);
    if (isNaN(cap) || cap < 1 || cap > 500) { setErr("Capacity must be 1–500"); return; }
    setLoading(true);
    try {
      const res = await api.createAgent({ name: form.name, capacity: cap, notes: form.notes });
      setAddModal(false);
      setKeyModal({ name: res.name, api_key: res.api_key, id: res.id, server: SERVER });
      setForm({ name: "", capacity: "60", notes: "" });
      setErr(""); load(); onRefresh?.();
    } catch (e) { setErr(e.message); }
    setLoading(false);
  };

  const regenKey = async (agent) => {
    setConfirmRegen(null);
    try { const res = await api.regenAgentKey(agent.id); setKeyModal({ name: agent.name, api_key: res.api_key, id: agent.id, server: SERVER }); load(); onRefresh?.(); } catch (e) { /* ignore */ }
  };

  const deleteAgent = async (agent) => {
    setConfirmDel(null);
    try {
      // Optimistic UI
      setAgents(prev => prev.filter(a => a.id !== agent.id));
      await api.deleteAgent(agent.id);
      load(); onRefresh?.();
    } catch (e) {
      load(); // restore
    }
  };

  const linuxCmd = (n, k) => `export BOTCROWD_API_KEY="${k}" BOTCROWD_AGENT_NAME="${n}" && bash <(curl -s ${SERVER}/agent_files/install.sh)`;
  const cmdCmd = (n, k) => `set BOTCROWD_API_KEY=${k}& set BOTCROWD_AGENT_NAME=${n}& powershell -Command "iwr ${SERVER}/agent_files/install.ps1 -UseBasicParsing | iex"`;
  const psCmd  = (n, k) => `$env:BOTCROWD_AGENT_NAME="${n}"; $env:BOTCROWD_API_KEY="${k}"; powershell -Command "iwr ${SERVER}/agent_files/install.ps1 -UseBasicParsing | iex"`;

  const copyCmd = (n, k) => { navigator.clipboard?.writeText(cmdCmd(n, k)); setCopied(true); setTimeout(() => setCopied(false), 2000); };
  const copyLinuxCmd = (n, k) => { navigator.clipboard?.writeText(linuxCmd(n, k)); setCopiedLinux(true); setTimeout(() => setCopiedLinux(false), 2000); };

  return (
    <div style={{ animation: "floatUp .35s ease" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#f1f5f9", letterSpacing: "-.4px" }}>RDP Servers</div>
          <div style={{ fontSize: 12, color: "#4a6280", marginTop: 2 }}>
            {online.length}/{agents.length} online · {totalActive}/{totalCap} bots active
          </div>
        </div>
        <button className="adm-btn adm-btn-primary" onClick={() => setAddModal(true)}>
          <PlusIcon /> Add Server
        </button>
      </div>

     {/* Stats row */}
<div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 14, marginBottom: 20 }}>
  {[
    { label: "Online Servers", val: online.length,  c: "#4ade80", k: "green"  },
    { label: "Total Capacity", val: totalCap,       c: "#38bdf8", k: "blue"   },
    { label: "Active Bots",    val: totalActive,    c: "#c084fc", k: "purple" },
  ].map((s, i) => (
    <div key={i} className="adm-stat-card" data-c={s.k} style={{ animationDelay: `${i * 0.06}s`, padding: "18px 20px" }}>
      <div className="adm-stat-bar" style={{ background: `linear-gradient(90deg,transparent,${s.c},transparent)`, boxShadow: `0 0 12px ${s.c}80` }} />
      <div className="adm-stat-val" style={{ color: s.c, fontSize: 36 }}>{s.val}</div>
      <div className="adm-stat-lbl" style={{ color: "#f1f5f9", fontWeight: 800, textShadow: "0 0 8px rgba(241,245,249,0.6)" }}>{s.label}</div>
    </div>
  ))}
</div>

{/* Servers grid */}
{fetching ? (
  <div className="adm-agent-grid" style={{ marginBottom: 22 }}>
    {[1,2,3].map(i => (
      <div key={i} className="adm-card adm-agent-card" style={{ padding: 18, gap: 13 }}>
        {[80,120,60,40].map((w,j) => (
          <div key={j} style={{ height: 14, width: w, borderRadius: 6, background: "rgba(255,255,255,0.06)", animation: "pulse 1.5s infinite" }} />
        ))}
      </div>
    ))}
  </div>
) : (
  <div className="adm-agent-grid" style={{ marginBottom: 22 }}>
    {agents.length === 0 && (
      <div className="adm-card" style={{ textAlign: "center", padding: "48px 20px", color: "#334155", gridColumn: "1/-1" }}>
        No servers yet. Click <b style={{ color: "#38bdf8" }}>Add Server</b> to create one.
      </div>
    )}
    {agents.map(a => {
      const p = pct(a.active_bots, a.capacity);
      return (
        <div key={a.id} className="adm-card adm-agent-card">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: "#e2e8f0", marginBottom: 3 }}>{a.name}</div>
              <div className="adm-mono" style={{ color: "#f1f5f9", fontWeight: 700, textShadow: "0 0 8px rgba(241,245,249,0.5)" }}>{a.ip || "Not connected yet"}</div>
            </div>
           <span style={{
  display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700,
  color: a.status === "online" ? "#4ade80" : "#f87171",
  background: a.status === "online" ? "rgba(74,222,128,0.1)" : "rgba(248,113,113,0.1)",
  border: `1px solid ${a.status === "online" ? "rgba(74,222,128,0.3)" : "rgba(248,113,113,0.3)"}`,
  borderRadius: 20, padding: "3px 10px",
}}>
  <span style={{ width: 6, height: 6, borderRadius: "50%", background: a.status === "online" ? "#4ade80" : "#f87171", boxShadow: a.status === "online" ? "0 0 7px #4ade80" : "0 0 7px #f87171" }} />
  {a.status === "online" ? "Online" : "Offline"}
</span>
          </div>
          {a.notes && <div style={{ fontSize: 11, color: "#4a6280" }}>{a.notes}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            {[
              { val: a.status === "online" ? a.active_bots : "—", lbl: "Active",   c: "#4ade80" },
              { val: a.capacity,                                    lbl: "Capacity", c: null      },
              { val: a.status === "online" ? `${p}%` : "—",        lbl: "Load",     c: p > 80 ? "#f87171" : p > 60 ? "#fbbf24" : null },
            ].map((m, i) => (
              <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 9, padding: 10, textAlign: "center" }}>
                <div style={{ fontSize: 20, fontWeight: 800, color: m.c || "#e2e8f0", lineHeight: 1.2 }}>{m.val}</div>
                <div style={{ fontSize: 9, color: "#f1f5f9", fontWeight: 800, textTransform: "uppercase", letterSpacing: .5, marginTop: 2, textShadow: "0 0 6px rgba(241,245,249,0.5)" }}>{m.lbl}</div>
              </div>
            ))}
          </div>
          {a.status === "online" && (
            <div className="adm-prog">
              <div className="adm-prog-fill" style={{ width: `${p}%`, background: p >= 90 ? "#f87171" : p >= 70 ? "#fbbf24" : "linear-gradient(90deg,#38bdf8,#818cf8)" }} />
            </div>
          )}
          <div style={{ fontSize: 11, color: "#f1f5f9", fontWeight: 700, textShadow: "0 0 6px rgba(241,245,249,0.5)" }}>Last seen: {a.status === "online" ? "just now" : fmtTime(a.last_seen)}</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button className="adm-btn adm-btn-sm" style={{ color: "#ffffff", borderColor: "rgba(255,255,255,0.22)" }} onClick={() => setKeyModal({ name: a.name, api_key: a.api_key || "(hidden)", id: a.id, server: SERVER })}>
  <TerminalIcon /> Command
</button>
            <button className="adm-btn adm-btn-sm" style={{ color: "#fbbf24", borderColor: "rgba(251,191,36,0.22)" }} onClick={() => setConfirmRegen(a)}>
              <RefreshIcon /> New Key
            </button>
            <button className="adm-btn adm-btn-sm adm-btn-danger" onClick={() => setConfirmDel(a)}>
              <TrashIcon />
            </button>
          </div>
        </div>
      );
    })}
  </div>
)}
      {/* Add modal */}
      <div className={`adm-modal-bg${addModal ? " open" : ""}`} onClick={e => e.target === e.currentTarget && setAddModal(false)}>
        <div className="adm-modal" style={{ width: 460 }}>
          <div className="adm-modal-head">
            <div className="adm-modal-title">Add New Server</div>
            <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setAddModal(false)}><XIcon /></button>
          </div>
          <p style={{ fontSize: 13, color: "#4a6280", marginBottom: 18, lineHeight: 1.6 }}>
            Name your server (e.g. <code style={{ color: "#fbbf24" }}>agent-a</code>). A unique PowerShell install command will be generated.
          </p>
          <div className="adm-form-row">
            <label className="adm-label">Server Name</label>
            <input ref={nameInputRef} className="adm-input" placeholder="e.g. agent-a" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
          </div>
          <div className="adm-form-row">
            <label className="adm-label">Capacity (max bots)</label>
            <input type="number" min="1" max="500" className="adm-input" value={form.capacity} onChange={e => setForm(p => ({ ...p, capacity: e.target.value }))} />
          </div>
          <div className="adm-form-row">
            <label className="adm-label">Notes (optional)</label>
            <input className="adm-input" placeholder="e.g. US East, Windows 10" value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
          </div>
          {err && <div className="adm-form-err">{err}</div>}
          <div className="adm-modal-footer">
            <button className="adm-btn" onClick={() => { setAddModal(false); setErr(""); }}>Cancel</button>
            <button className="adm-btn adm-btn-primary" onClick={createAgent} disabled={loading}>{loading ? "Creating…" : "Create & Get Command"}</button>
          </div>
        </div>
      </div>

      {/* Key modal */}
      {keyModal && (
        <div className="adm-modal-bg open" onClick={e => e.target === e.currentTarget && setKeyModal(null)}>
          <div className="adm-modal" style={{ width: 640 }}>
            <div className="adm-modal-head">
              <div className="adm-modal-title">Connect "{keyModal.name}"</div>
              <button className="adm-btn adm-btn-ghost adm-btn-sm" onClick={() => setKeyModal(null)}><XIcon /></button>
            </div>
            <p style={{ fontSize: 13, color: "#4a6280", marginBottom: 14, lineHeight: 1.6 }}>
              Paste one of the commands below on your RDP machine. It installs everything and connects automatically.
            </p>
            <label className="adm-label">API KEY</label>
            <div className="adm-mono-box" style={{ color: "#fbbf24", marginBottom: 14, fontSize: 14, fontWeight: 700 }}>{keyModal.api_key}</div>

            <label className="adm-label">⌨️ CMD (Command Prompt) — <span style={{ color: "#4ade80" }}>recommended</span></label>
            <div className="adm-mono-box" style={{ color: "#ffffff", marginBottom: 14, fontSize: 13, fontWeight: 600, textShadow: "0 0 8px rgba(255,255,255,0.4)" }}>{cmdCmd(keyModal.name, keyModal.api_key)}</div>

            <label className="adm-label">🔵 PowerShell</label>
            <div className="adm-mono-box" style={{ color: "#ffffff", marginBottom: 14, fontSize: 13, fontWeight: 600, textShadow: "0 0 8px rgba(255,255,255,0.4)" }}>{psCmd(keyModal.name, keyModal.api_key)}</div>


            <label className="adm-label">🐧 Linux / SSH</label>
            <div className="adm-mono-box" style={{ color: "#4ade80", marginBottom: 14, fontSize: 13, fontWeight: 600, textShadow: "0 0 8px rgba(74,222,128,0.4)" }}>{linuxCmd(keyModal.name, keyModal.api_key)}</div>
            <div style={{ background: "rgba(56,189,248,0.06)", border: "1px solid rgba(56,189,248,0.15)", borderRadius: 10, padding: "10px 14px", fontSize: 12, color: "#94a3b8" }}>
              💡 <b>Tip:</b> Open <b>cmd.exe</b> and paste the first command. The server registers as <span style={{ fontFamily: "monospace", color: "#fbbf24" }}>{keyModal.name}</span> automatically.
            </div>
            <div className="adm-modal-footer">
              <button className="adm-btn" onClick={() => copyCmd(keyModal.name, keyModal.api_key)}>{copied ? "✅ Copied!" : "📋 Copy CMD Command"}</button>
              <button className="adm-btn" onClick={() => copyLinuxCmd(keyModal.name, keyModal.api_key)}>{copiedLinux ? "✅ Copied!" : "🐧 Copy Linux Command"}</button>
              <button className="adm-btn adm-btn-primary" onClick={() => setKeyModal(null)}>Done</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmModal
        open={!!confirmDel}
        title="Delete Server?"
        message={<>Are you sure you want to delete server <strong style={{color:"#f87171"}}>{confirmDel?.name}</strong>? This cannot be undone.</>}
        confirmLabel="Delete"
        onConfirm={() => deleteAgent(confirmDel)}
        onCancel={() => setConfirmDel(null)}
      />
      <ConfirmModal
        open={!!confirmRegen}
        title="Regenerate API Key?"
        message={<>Regenerate API key for <strong style={{color:"#fbbf24"}}>{confirmRegen?.name}</strong>? The old install command will stop working.</>}
        confirmLabel="Regenerate"
        danger={false}
        onConfirm={() => regenKey(confirmRegen)}
        onCancel={() => setConfirmRegen(null)}
      />
    </div>
  );
}

function PlusIcon()     { return <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function TerminalIcon() { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 17 10 11 4 5"/><line x1="12" y1="19" x2="20" y2="19"/></svg>; }
function RefreshIcon()  { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>; }
function TrashIcon()    { return <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>; }
function XIcon()        { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
